import PageHeader from "../components/PageHeader";
import SEOMeta from "../components/SEOMeta";

const STANDARDS = [
  "Confidentiality of client information, without exception.",
  "Active management of conflict of interest, declared in writing at engagement start.",
  "Senior accountability for every engagement.",
  "Honesty about what strategy can and cannot deliver."
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEOMeta
        title="About"
        description="Dugavu Ventures is a specialist global consulting firm offering corporate strategy and advisory in minerals, mining, and oil and gas. Senior-led delivery with an Africa-resident perspective."
        url="/about"
      />
      <PageHeader
        eyebrow="About"
        title="A specialist firm. Built for serious institutions."
        lede="Dugavu Ventures was established to occupy the space between the global strategy houses and the local generalist consultancies — a third option built around senior delivery, sectoral depth, and an Africa-resident perspective."
        image="https://images.pexels.com/photos/2014422/pexels-photo-2014422.jpeg?auto=compress&cs=tinysrgb&w=1800&q=80"
        imageAlt="East African cityscape"
        testid="about-header"
      />

      {/* Our story */}
      <section className="py-20 md:py-28" data-testid="about-story">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
            <div className="md:col-span-3">
              <div className="eyebrow">Our story</div>
            </div>
            <div className="md:col-span-9">
              <p className="text-lg md:text-xl text-charcoal leading-relaxed max-w-3xl">
                Dugavu Ventures was founded on the conviction that Africa's state-owned enterprises and resource-sector companies deserve advisory work that combines international rigour with deep local context — delivered by senior people who stay engaged through delivery. We exist because too often the choice has been between a global firm that flies in for the deck and a local generalist that lacks specialist depth. We are neither. We are the third option.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Where we sit + Group affiliation */}
      <section className="py-20 md:py-28 bg-subtle" data-testid="about-where">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-ink leading-tight border-t border-oxblood pt-6">
                Where we sit
              </h2>
              <p className="mt-6 text-charcoal text-lg leading-relaxed">
                The firm is headquartered in Kampala, Uganda, and serves clients across East and Central Africa. Dugavu Ventures Ltd is incorporated in Uganda and registered with the relevant regulatory and procurement authorities.
              </p>
            </div>
            <div>
              <h2 className="font-serif text-3xl md:text-4xl text-ink leading-tight border-t border-oxblood pt-6">
                Group affiliation
              </h2>
              <p className="mt-6 text-charcoal text-lg leading-relaxed">
                Dugavu Ventures is part of the Dagala Group, an international group of companies headquartered in the United Kingdom. The group affiliation gives Dugavu access to international expertise, standards, and a wider professional network. The firm's work, however, is led, staffed, and delivered from Africa.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-24 md:py-32" data-testid="about-leadership">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16">
            <div className="md:col-span-3">
              <div className="eyebrow">Leadership</div>
            </div>
            <div className="md:col-span-9">
              <h2 className="display-2 text-3xl md:text-4xl lg:text-5xl text-ink max-w-3xl leading-tight">
                Engagements are partner-led. The people you meet at first contact are the people on the work.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 border-t border-rule pt-12" data-testid="leadership-john-kyagulanyi">
            <div className="md:col-span-4">
              <div className="aspect-[4/5] overflow-hidden bg-subtle border border-rule">
                <img
                  src="https://customer-assets.emergentagent.com/job_dugavu-staging/artifacts/i674c8q3_John.jpeg"
                  alt="John Kyagulanyi"
                  className="w-full h-full object-cover photo-treat"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="md:col-span-8">
              <div className="text-[11px] tracking-widest uppercase font-mono text-slate">Founder & Principal</div>
              <h3 className="font-serif text-3xl md:text-4xl text-ink mt-3 leading-tight">John Kyagulanyi</h3>

              <p className="mt-6 text-charcoal text-lg leading-relaxed max-w-2xl">
                John leads Dugavu Ventures and the firm's work with state-owned enterprises and resource-sector clients across East and Central Africa. His practice combines corporate strategy, public-sector reform, and resource-sector commercial advisory — built across senior finance and operating roles in global industry and through his own advisory ventures. He brings deep multinational experience, having worked across more than 40 countries spanning Africa, Europe, the Middle East, and the Americas.
              </p>
              <p className="mt-4 text-charcoal text-lg leading-relaxed max-w-2xl">
                Before establishing Dugavu Ventures, John served as Global Finance Director at Reckitt (RB), where he led the finance function for close to five years, embedding controls, FP&A discipline, and operating cadence required to drive strategic growth and rapid business expansion. Earlier, as Finance Director for Distributor Markets, EMEA at Indivior, he held commercial and financial accountability across a multi-country distributor network. Alongside his industry career he founded Dagala Group, a cross-continental advisory and venture platform focused on governance, compliance, and cross-border growth between the UK, Europe, the Middle East, and Africa.
              </p>
              <p className="mt-4 text-charcoal text-lg leading-relaxed max-w-2xl">
                He holds personal accountability for every engagement he leads, from mandate alignment through to delivery and handover. Clients meet him at first contact and continue to work with him through the term of the work.
              </p>

              <dl className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-px bg-rule border-t border-b border-rule">
                <div className="bg-bone p-5">
                  <dt className="text-[10px] tracking-widest uppercase font-mono text-slate">Professional qualifications</dt>
                  <dd className="mt-2 font-serif text-lg text-ink leading-relaxed">
                    MBA, Manchester Business School<br />
                    FCCA — Fellow of the Association of Chartered Certified Accountants
                  </dd>
                </div>
                <div className="bg-bone p-5">
                  <dt className="text-[10px] tracking-widest uppercase font-mono text-slate">Languages</dt>
                  <dd className="mt-2 font-serif text-lg text-ink leading-relaxed">
                    English<br />
                    Swahili<br />
                    Luganda
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* Registration & compliance */}
      <section className="py-20 md:py-28 bg-ink text-bone grain relative" data-testid="about-compliance">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
            <div className="md:col-span-4">
              <div className="text-[11px] tracking-widest uppercase font-mono text-earth">Registration & compliance</div>
              <h2 className="display-2 mt-4 text-3xl md:text-4xl lg:text-5xl text-bone leading-tight">
                Properly constituted. Properly accountable.
              </h2>
            </div>
            <div className="md:col-span-8 md:pl-8 md:border-l md:border-bone/15">
              <dl className="text-bone/85">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-6 border-b border-bone/15">
                  <dt className="text-[11px] tracking-widest uppercase font-mono text-earth">Incorporation</dt>
                  <dd className="md:col-span-2 text-lg">
                    Incorporated in Uganda — Uganda Registration Services Bureau registration number{" "}
                    <span className="font-mono text-bone">G251006-2891</span>
                  </dd>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-6 border-b border-bone/15">
                  <dt className="text-[11px] tracking-widest uppercase font-mono text-earth">Tax</dt>
                  <dd className="md:col-span-2 text-lg">
                    Tax registered with Uganda Revenue Authority — TIN{" "}
                    <span className="font-mono text-bone">1055704464</span>
                  </dd>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-6 border-b border-bone/15">
                  <dt className="text-[11px] tracking-widest uppercase font-mono text-earth">Procurement</dt>
                  <dd className="md:col-span-2 text-lg">
                    Aligned to the procurement standards of the Public Procurement and Disposal of Public Assets Authority.
                  </dd>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-6">
                  <dt className="text-[11px] tracking-widest uppercase font-mono text-earth">Employment</dt>
                  <dd className="md:col-span-2 text-lg">
                    National Social Security Fund and statutory employment compliance.
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* Standards */}
      <section className="py-24 md:py-32" data-testid="about-standards">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="md:col-span-3">
              <div className="eyebrow">Standards we hold ourselves to</div>
            </div>
            <div className="md:col-span-9">
              <h2 className="display-2 text-3xl md:text-4xl lg:text-5xl text-ink max-w-3xl leading-tight">
                The principles by which we work.
              </h2>
            </div>
          </div>

          <ul className="border-t border-rule">
            {STANDARDS.map((s, i) => (
              <li
                key={s}
                className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 py-8 border-b border-rule"
                data-testid={`standard-${i}`}
              >
                <div className="md:col-span-3">
                  <span className="numeral text-2xl">0{i + 1}</span>
                </div>
                <div className="md:col-span-9">
                  <p className="font-serif text-2xl md:text-3xl text-ink leading-snug max-w-3xl">{s}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
